import {
  IndexDataTable,
  getIndexPaginationMeta,
  IndexTableClearFiltersButton,
  IndexTableExportButton,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import {
  fetchAllIndexTablePages,
  fetchIndexTableQueryPage,
  useIndexTableSpreadsheetExport,
} from '@/components/tables/indexTableExport'
import { getProductIndexColumns } from '@/components/tables/products/columns'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { ProductStorefrontVisibility } from '@/lib/enums/ProductStorefrontVisibility'
import { ProductCatalogType } from '@/lib/product-catalog-type'
import queryClient from '@/lib/queryClient'
import { productFilters, productQueries, useProducts } from '@/lib/services/products/products.query'
import {
  Link,
  createFileRoute,
  redirect,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import {
  CalendarClock,
  Eye,
  EyeOff,
  Flower2,
  FolderTree,
  PackagePlus,
  SlidersHorizontal,
} from 'lucide-react'
import { useMemo } from 'react'
import { z } from 'zod/v4'

const DEFAULT_PAGE_SIZE = 20
export const PRODUCT_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'Catalog items', icon: <SlidersHorizontal className="size-4" /> },
  { value: 'products', label: 'Products', icon: <Flower2 className="size-4" /> },
  { value: 'add-ons', label: 'Add-ons', icon: <PackagePlus className="size-4" /> },
  {
    value: 'subscription-packages',
    label: 'Subscription packages',
    icon: <CalendarClock className="size-4" />,
  },
]

export const defaultProductListSearchParams = {
  type: 'all',
  storefront_visibility: 'all',
  sort: 'name_asc',
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
}
export const strippedProductListSearchParams = {
  type: defaultProductListSearchParams.type,
  storefront_visibility: defaultProductListSearchParams.storefront_visibility,
  sort: defaultProductListSearchParams.sort,
  page: defaultProductListSearchParams.page,
  page_size: defaultProductListSearchParams.page_size,
}
const productStorefrontVisibilityFilterValues = [
  'all',
  ProductStorefrontVisibility.Always.Value,
  ProductStorefrontVisibility.PublishedCollection.Value,
  ProductStorefrontVisibility.Hidden.Value,
]
const productStorefrontVisibilityFilterOptions = [
  {
    value: 'all',
    label: 'All visibility',
    icon: <SlidersHorizontal className="size-4" />,
  },
  {
    value: ProductStorefrontVisibility.Always.Value,
    label: ProductStorefrontVisibility.Always.Label,
    icon: <Eye className="size-3.5" />,
  },
  {
    value: ProductStorefrontVisibility.PublishedCollection.Value,
    label: ProductStorefrontVisibility.PublishedCollection.Label,
    icon: <FolderTree className="size-3.5" />,
  },
  {
    value: ProductStorefrontVisibility.Hidden.Value,
    label: ProductStorefrontVisibility.Hidden.Label,
    icon: <EyeOff className="size-3.5" />,
  },
]
const productSortValues = [
  'relevance',
  'name_asc',
  'name_desc',
  'category_asc',
  'category_desc',
  'storefront_visibility_asc',
  'storefront_visibility_desc',
  'variants_asc',
  'variants_desc',
  'updated_asc',
  'updated_desc',
  'created_asc',
  'created_desc',
]
const productIndexBaseFilters = productFilters().get()
export const productListSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  type: z
    .enum(['all', 'products', 'add-ons', 'subscription-packages'])
    .catch(defaultProductListSearchParams.type),
  storefront_visibility: z
    .enum(productStorefrontVisibilityFilterValues)
    .catch(defaultProductListSearchParams.storefront_visibility),
  sort: z.enum(productSortValues).catch(defaultProductListSearchParams.sort),
  page: z.coerce.number().int().positive().catch(defaultProductListSearchParams.page),
  page_size: z.coerce
    .number()
    .int()
    .positive()
    .max(100)
    .catch(defaultProductListSearchParams.page_size),
})

export const Route = createFileRoute('/_authenticated/$orgId/products/all-products')({
  validateSearch: (search) => productListSearchSchema.parse(search),
  search: {
    middlewares: [stripSearchParams(strippedProductListSearchParams)],
  },
  loaderDeps: ({ search }) => ({ search: getProductListSearchParams(search) }),
  loader: ({ params, deps: { search } }) => {
    throw redirect({
      to: '/$orgId/products',
      params: { orgId: params.orgId },
      search,
      replace: true,
    })
  },
})

export function getProductListSearchParams(search) {
  return productListSearchSchema.parse(search ?? {})
}

export function getProductListFilters(search) {
  const parsedSearch = getProductListSearchParams(search)
  const trimmedSearch = parsedSearch.search?.trim()

  return {
    ...productIndexBaseFilters,
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
    ...(parsedSearch.type === 'products'
      ? { product_catalog_type: ProductCatalogType.Standard, is_add_on_only: false }
      : parsedSearch.type === 'add-ons'
        ? { product_catalog_type: ProductCatalogType.Standard, is_add_on: true }
        : parsedSearch.type === 'subscription-packages'
          ? { product_catalog_type: ProductCatalogType.SubscriptionPackage }
          : {}),
    ...(parsedSearch.storefront_visibility !== defaultProductListSearchParams.storefront_visibility
      ? { storefront_visibility: parsedSearch.storefront_visibility }
      : {}),
    sort: parsedSearch.sort,
    page: parsedSearch.page,
    page_size: parsedSearch.page_size,
  }
}

function getProductsFromResponse(response) {
  return Array.isArray(response) ? response : (response?.data ?? [])
}

function productCountText(total, type = defaultProductListSearchParams.type) {
  if (type === 'add-ons') {
    return `${total} add-on${total === 1 ? '' : 's'}`
  }

  if (type === 'subscription-packages') {
    return `${total} subscription package${total === 1 ? '' : 's'}`
  }

  return `${total} catalog item${total === 1 ? '' : 's'}`
}

function productEmptyStateText(type = defaultProductListSearchParams.type) {
  if (type === 'add-ons') {
    return 'No add-ons found.'
  }

  if (type === 'products') {
    return 'No products found.'
  }

  if (type === 'subscription-packages') {
    return 'No subscription packages found.'
  }

  return 'No catalog items found.'
}

function ProductTypeFilterSelect({ value, onValueChange }) {
  const isFiltered = value !== defaultProductListSearchParams.type

  return (
    <div className="flex items-center">
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          aria-label="Filter product type"
          selected={isFiltered}
          className="w-fit min-w-[9rem]"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRODUCT_TYPE_FILTER_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.icon}
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function ProductVisibilityFilterSelect({ value, onValueChange }) {
  const isFiltered = value !== defaultProductListSearchParams.storefront_visibility

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label="Filter products by website visibility"
        selected={isFiltered}
        className="w-fit"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {productStorefrontVisibilityFilterOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.icon}
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function ProductListTable({ orgId, search }) {
  const navigate = useNavigate()
  const path = usePathHelper()
  const urlSearch = getProductListSearchParams(search)
  const urlSearchTerm = urlSearch.search ?? ''
  const hasActiveSearch = urlSearchTerm.trim().length > 0
  const hasActiveTypeFilter = urlSearch.type !== defaultProductListSearchParams.type
  const hasActiveVisibilityFilter =
    urlSearch.storefront_visibility !== defaultProductListSearchParams.storefront_visibility
  const { updatePage, updatePageSize, updateSearchTerm, updateSort, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: getProductListSearchParams,
      getSearchTermUpdates: (searchTerm) => ({
        sort: searchTerm ? 'relevance' : defaultProductListSearchParams.sort,
      }),
      searchTerm: urlSearchTerm,
    })
  const productListFilters = getProductListFilters(urlSearch)
  const {
    data: productsResponse,
    isFetching,
    isPending,
  } = useProducts({
    organizationId: orgId,
    filters: productListFilters,
  })
  const products = getProductsFromResponse(productsResponse)
  const paginationMeta = getIndexPaginationMeta(
    productsResponse,
    urlSearch.page,
    urlSearch.page_size
  )
  const columns = useMemo(() => getProductIndexColumns(), [])
  const { exportDisabled, exportPending, handleExport } = useIndexTableSpreadsheetExport({
    columns,
    rowCount: paginationMeta.total,
    filename: 'products.xlsx',
    sheetName: 'Products',
    loadRows: ({ signal } = {}) =>
      fetchAllIndexTablePages({
        signal,
        fetchPage: ({ page, pageSize, signal }) =>
          fetchIndexTableQueryPage(
            productQueries.index({
              organizationId: orgId,
              filters: {
                ...productListFilters,
                page,
                page_size: pageSize,
              },
            }),
            { signal }
          ),
      }),
  })

  const handlePageIntent = (page) => {
    void queryClient.prefetchQuery(
      productQueries.index({
        organizationId: orgId,
        filters: {
          ...productListFilters,
          page,
        },
      })
    )
  }

  const handleVisibilityChange = (storefrontVisibility) =>
    updateTableParams({ storefront_visibility: storefrontVisibility })

  const clearProductFilters = () =>
    updateTableParams({
      search: undefined,
      sort: defaultProductListSearchParams.sort,
      type: undefined,
      storefront_visibility: undefined,
    })

  return (
    <div className="space-y-4">
      <IndexDataTable
        data={products}
        columns={columns}
        sort={urlSearch.sort}
        onSortChange={updateSort}
        rowLink={(product) => path.products.show(product.id).link}
        getRowId={(product) => String(product.id)}
        isLoading={isPending}
        toolbar={
          <IndexTableToolbar
            primary={
              <IndexTableSearchBox
                value={urlSearchTerm}
                onCommit={updateSearchTerm}
                countText={productCountText(paginationMeta.total, urlSearch.type)}
                isFetching={isFetching}
                placeholder="Search products..."
              />
            }
            secondary={
              <>
                {hasActiveSearch || hasActiveTypeFilter || hasActiveVisibilityFilter ? (
                  <IndexTableClearFiltersButton onClick={clearProductFilters} />
                ) : null}
                <ProductVisibilityFilterSelect
                  value={urlSearch.storefront_visibility}
                  onValueChange={handleVisibilityChange}
                />
              </>
            }
            actions={
              <>
                {urlSearch.type === 'add-ons' ? (
                  <Button asChild variant="outline">
                    <Link {...path.addOnSets.index().link}>
                      <PackagePlus className="size-4" /> Add-on Collections
                    </Link>
                  </Button>
                ) : null}
                <ProductTypeFilterSelect
                  value={urlSearch.type}
                  onValueChange={(value) =>
                    updateTableParams({
                      type: value === defaultProductListSearchParams.type ? undefined : value,
                    })
                  }
                />
                <IndexTableExportButton
                  disabled={exportDisabled}
                  isPending={exportPending}
                  onClick={handleExport}
                />
              </>
            }
          />
        }
        emptyState={
          <Typography variant="supporting">{productEmptyStateText(urlSearch.type)}</Typography>
        }
        tableClassName="text-sm"
      />

      <IndexTablePagination
        meta={paginationMeta}
        pageSize={urlSearch.page_size}
        onPageChange={updatePage}
        onPageIntent={handlePageIntent}
        onPageSizeChange={updatePageSize}
      />
    </div>
  )
}
